import type { ReactNode } from "react";
import { cn } from "../utils/cn";
import type { ButtonIntent, ButtonSize, ButtonState } from "./Button";

type IconButtonProps = {
  icon: ReactNode;
  ariaLabel: string;
  intent?: ButtonIntent;
  size?: ButtonSize;
  state?: ButtonState;
  onClick?: () => void;
};

const base = "inline-flex items-center justify-center rounded-md font-semibold transition-colors hover:opacity-80";
const intents: Record<ButtonIntent, string> = {
  default: "bg-surface text-text border border-border hover:bg-surface-muted",
  primary: "bg-primary text-primary-foreground border border-primary hover:bg-primary/90",
  danger: "bg-danger text-primary-foreground border border-danger hover:bg-danger/90",
  ghost: "bg-transparent text-text border border-transparent hover:bg-surface-muted hover:border-border",
};
const sizes: Record<ButtonSize, string> = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-sm",
};

const stateClasses: Record<ButtonState, string> = {
  default: "",
  hover: "",
  active: "",
  focus: "outline outline-2 outline-primary/40 outline-offset-2",
  disabled: "opacity-50 pointer-events-none",
  loading: "opacity-70",
};

const hoverByIntent: Record<ButtonIntent, string> = {
  default: "bg-surface-muted",
  primary: "bg-primary/90",
  danger: "bg-danger/90",
  ghost: "bg-surface-muted",
};

const activeByIntent: Record<ButtonIntent, string> = {
  default: "bg-border/40",
  primary: "bg-primary/80",
  danger: "bg-danger/80",
  ghost: "bg-border/40",
};

export function IconButton({
  icon,
  ariaLabel,
  intent = "default",
  size = "md",
  state = "default",
  onClick,
}: IconButtonProps) {
  const intentState =
    state === "hover" ? hoverByIntent[intent] : state === "active" ? activeByIntent[intent] : stateClasses[state];
  const isDisabled = state === "disabled" || state === "loading";
  return (
    <button
      className={cn(base, intents[intent], sizes[size], intentState)}
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={isDisabled}
    >
      {state === "loading" ? "…" : icon}
    </button>
  );
}
